<template>
    <slot v-bind="{
        ...state,
        setButton,
        setPopover,
    }" />
</template>

<script setup>
import { onBeforeMount, onMounted, reactive } from 'vue';
import { createPopper } from '@popperjs/core';

import { getCartExpiration, isBasketTimerHidden } from '../utils/cookies';
import pluralize from '../utils/pluralize';

const state = reactive({
    countdown: null,
    announcement: null,
    expiration: null,
});

let timeout;
let button;
let popover;

const tick = () => {
    clearTimeout(timeout);

    const now = new Date();
    const future = new Date(state.expiration);
    const delta = future.getTime() - now.getTime();

    if (delta < 0) {
        state.expiration = null;
        window.location.href = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/booking/basket`;
        return;
    }

    const seconds = delta / 1000;
    const minutes = seconds / 60;

    const remainingMinutes = Math.floor(minutes);
    const remainingSeconds = Math.round(minutes % 1 * 60);

    const countdown = [];

    if (remainingMinutes) {
        countdown.push(`${pluralize(remainingMinutes, 'minute')},`);
    }

    countdown.push(`${pluralize(remainingSeconds, 'second')} remaining to complete purchase`);

    state.countdown = countdown.join(' ');

    const totalSecondsRemaining = Math.round(seconds);

    if (!state.announcement) {
        state.announcement = `You have ${pluralize(remainingMinutes, 'minute')} and ${pluralize(remainingSeconds, 'second')} remaining to complete your purchase.`;
    } else if (totalSecondsRemaining > 60) {
        if (remainingSeconds === 0 && remainingMinutes % 1 === 0) {
            state.announcement = `You have ${pluralize(remainingMinutes, 'minute')} remaining to complete your purchase.`;
        }
    } else {
        // Under 1 minute — announce at 30s, 10s, 5s
        if (totalSecondsRemaining === 30) {
            state.announcement = 'You have 30 seconds remaining to complete your purchase.';
        } else if (totalSecondsRemaining === 10) {
            state.announcement = 'You have 10 seconds remaining to complete your purchase.';
        } else if (totalSecondsRemaining === 5) {
            state.announcement = 'You have 5 seconds remaining to complete your purchase.';
        } else if (totalSecondsRemaining === 60) {
            state.announcement = 'You have less than a minute remaining to complete your purchase.';
        }
    }

    timeout = setTimeout(tick, 1000);
}

const setButton = element => {
    button = element;
}

const setPopover = element => {
    popover = element;
}

const isTouchDevice = () => {
    return window.matchMedia('(hover: none)').matches;
}

onBeforeMount(() => {
    state.expiration = getCartExpiration();

    if (state.expiration && !isBasketTimerHidden()) {
        tick();
    }
});

onMounted(() => {
    if (!button || !popover) {
        return;
    }

    createPopper(button, popover, {
        placement: 'bottom-end',
    });

    button.addEventListener('mouseover', () => {
        if (!isTouchDevice()) {
            popover.showPopover({
                source: button,
            });
        }
    });

    button.addEventListener('mouseout', () => {
        if (!isTouchDevice()) {
            popover.hidePopover();
        }
    });

    button.addEventListener('click', () => {
        if (isTouchDevice()) {
            popover.showPopover({
                source: button,
            });
        }
    });

    document.addEventListener('click', event => {
        if (isTouchDevice() && !popover.contains(event.target) && !button.contains(event.target)) {
            popover.hidePopover();
        }
    });
});
</script>
